import { useState, useEffect, useCallback } from 'react';
import {
    myFilesService,
    MyFilesData,
    ProductFilesData,
} from '@/services/myFiles.service';

/**
 * Hook to fetch all purchased digital products for the current user
 */
export function useMyPurchasedProducts() {
    const [data, setData] = useState<MyFilesData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await myFilesService.getPurchasedProducts();

            if (response.success && response.data) {
                setData(response.data);
            } else {
                setError(response.error || 'Failed to fetch your files');
            }
        } catch (err) {
            setError('Failed to fetch your files');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    return {
        data,
        products: data?.products ?? [],
        totalProducts: data?.total_products ?? 0,
        totalFiles: data?.total_files ?? 0,
        loading,
        error,
        refetch: fetchProducts,
    };
}

/**
 * Hook to fetch files for a specific purchased product
 */
export function useProductFiles(productSlug: string | undefined, category?: string) {
    const [data, setData] = useState<ProductFilesData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchFiles = useCallback(async () => {
        if (!productSlug) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await myFilesService.getProductFiles(productSlug, category);

            if (response.success && response.data) {
                setData(response.data);
            } else {
                setError(response.error || 'Failed to fetch product files');
            }
        } catch (err) {
            setError('Failed to fetch product files');
        } finally {
            setLoading(false);
        }
    }, [productSlug, category]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    return {
        data,
        product: data?.product ?? null,
        files: data?.files ?? [],
        totalFiles: data?.total_files ?? 0,
        totalSizeBytes: data?.total_size_bytes ?? 0,
        loading,
        error,
        refetch: fetchFiles,
    };
}

/**
 * Hook to fetch file categories for a product
 */
export function useFileCategories(productSlug: string | undefined) {
    const [categories, setCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCategories = useCallback(async () => {
        if (!productSlug) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await myFilesService.getFileCategories(productSlug);

            if (response.success && response.data) {
                setCategories(response.data.categories);
            } else {
                setError(response.error || 'Failed to fetch categories');
            }
        } catch (err) {
            setError('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    }, [productSlug]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    return {
        categories,
        loading,
        error,
        refetch: fetchCategories,
    };
}

/**
 * Hook to check product access
 */
export function useProductAccess(productSlug: string | undefined) {
    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const checkAccess = useCallback(async () => {
        if (!productSlug) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await myFilesService.checkProductAccess(productSlug);

            if (response.success && response.data) {
                setHasAccess(response.data.has_access);
            } else {
                setHasAccess(false);
            }
        } catch (err) {
            setHasAccess(false);
        } finally {
            setLoading(false);
        }
    }, [productSlug]);

    useEffect(() => {
        checkAccess();
    }, [checkAccess]);

    return {
        hasAccess,
        loading,
        error,
        refetch: checkAccess,
    };
}

/**
 * Hook for file download with tracking
 */
export function useFileDownload() {
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const downloadFile = useCallback(async (productSlug: string, fileId: string, filename: string) => {
        try {
            setDownloading(true);
            setError(null);

            // Track and get download URL
            const response = await myFilesService.trackDownload(productSlug, fileId);

            if (response.success && response.data?.download_url) {
                // Open download in new tab or trigger download
                const link = document.createElement('a');
                link.href = response.data.download_url;
                link.download = filename;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                setError(response.error || 'Failed to get download URL');
            }
        } catch (err) {
            setError('Failed to download file');
        } finally {
            setDownloading(false);
        }
    }, []);

    return {
        downloadFile,
        downloading,
        error,
    };
}

export default useMyPurchasedProducts;
